/**
 * Lightweight Module Runtime
 *
 * Executes AOS modules in a restricted scope with a controlled bridge API.
 * No iframe sandbox - relies on signature verification and permission enforcement.
 */

import type { DAGStore } from '../dag/store.js';
import type { DAGObject } from '../dag/types.js';
import type { KeyPair } from '../identity/keys.js';
import { signObject, createSignedObject } from '../identity/signing.js';
import type {
  ModuleManifest,
  ModulePermission,
  LoadedModule,
} from './types.js';
import { PERMISSION_CATEGORIES } from './types.js';

// ============================================================================
// Bridge API Types
// ============================================================================

/**
 * Module information exposed to running modules
 */
export interface ModuleInfo {
  id: string;
  version: string;
  permissions: ModulePermission[];
}

/**
 * DAG query filter
 */
export interface DAGFilter {
  type?: string;
  creator?: string;
  since?: number;
  until?: number;
  limit?: number;
}

/**
 * Notification options
 */
export interface NotifyOptions {
  type?: 'info' | 'success' | 'warning' | 'error';
  duration?: number;
}

/**
 * AOS Bridge API - the interface modules use to interact with AOS
 */
export interface AOSBridgeAPI {
  /** Module metadata */
  readonly module: ModuleInfo;

  /** Identity operations */
  identity: {
    /** Get the user's public key (requires identity:read) */
    getPublicKey(): Promise<string>;
    /** Sign data with user's key (requires identity:sign, prompts user) */
    sign(data: Uint8Array): Promise<string>;
  };

  /** DAG storage operations */
  dag: {
    /** Get object by hash (requires dag:read) */
    get(hash: string): Promise<DAGObject | null>;
    /** Query objects (requires dag:read) */
    query(filter: DAGFilter): Promise<DAGObject[]>;
    /** Store an object (requires dag:write) */
    put(type: string, content: unknown): Promise<string>;
  };

  /** Module-local storage */
  storage: {
    /** Get value by key (requires storage:local) */
    get(key: string): Promise<unknown>;
    /** Set value (requires storage:local) */
    set(key: string, value: unknown): Promise<void>;
    /** Delete value (requires storage:local) */
    delete(key: string): Promise<void>;
    /** List all keys (requires storage:local) */
    keys(): Promise<string[]>;
  };

  /** Event system */
  events: {
    /** Subscribe to event */
    on(event: string, handler: (...args: unknown[]) => void): void;
    /** Unsubscribe from event */
    off(event: string, handler: (...args: unknown[]) => void): void;
    /** Emit event (module-scoped) */
    emit(event: string, ...args: unknown[]): void;
  };

  /** Logging (always available) */
  log: {
    debug(...args: unknown[]): void;
    info(...args: unknown[]): void;
    warn(...args: unknown[]): void;
    error(...args: unknown[]): void;
  };
}

// ============================================================================
// Runtime Configuration
// ============================================================================

/**
 * Runtime configuration
 */
export interface ModuleRuntimeConfig {
  /** DAG store for persistence */
  dagStore: DAGStore;
  /** User's identity for signing */
  identity?: KeyPair;
  /** Callback when module requests signing (for user approval) */
  onSignRequest?: (moduleId: string, data: Uint8Array) => Promise<boolean>;
  /** Callback for notifications */
  onNotify?: (moduleId: string, message: string, options?: NotifyOptions) => void;
  /** Callback for logging */
  onLog?: (moduleId: string, level: string, args: unknown[]) => void;
}

/**
 * Running module instance
 */
export interface RunningModule {
  /** Loaded module info */
  module: LoadedModule;
  /** Module's local storage */
  storage: Map<string, unknown>;
  /** Event handlers */
  eventHandlers: Map<string, Set<(...args: unknown[]) => void>>;
  /** Started timestamp */
  startedAt: number;
  /** Whether module is running */
  running: boolean;
}

/**
 * Module execution result
 */
export interface ModuleExecutionResult {
  success: boolean;
  error?: string;
  exports?: Record<string, unknown>;
}

// ============================================================================
// Permission Enforcement
// ============================================================================

/**
 * Check if a permission is granted
 */
function hasPermission(
  grantedPermissions: ModulePermission[],
  required: ModulePermission
): boolean {
  return grantedPermissions.includes(required);
}

/**
 * Create a permission error
 */
function permissionError(permission: ModulePermission): Error {
  return new Error(`Permission denied: ${permission}`);
}

// ============================================================================
// Module Runtime
// ============================================================================

/**
 * Lightweight module runtime
 *
 * Executes modules in a restricted scope with only the AOS bridge API available.
 */
export class ModuleRuntime {
  private config: ModuleRuntimeConfig;
  private runningModules: Map<string, RunningModule> = new Map();

  constructor(config: ModuleRuntimeConfig) {
    this.config = config;
  }

  /**
   * Execute a loaded module
   */
  async execute(loadedModule: LoadedModule): Promise<ModuleExecutionResult> {
    const { manifest, code, grantedPermissions } = loadedModule;

    // Check if already running
    if (this.runningModules.has(manifest.id)) {
      return {
        success: false,
        error: `Module ${manifest.id} is already running`,
      };
    }

    // Create running module state
    const runningModule: RunningModule = {
      module: loadedModule,
      storage: new Map(),
      eventHandlers: new Map(),
      startedAt: Date.now(),
      running: true,
    };

    this.runningModules.set(manifest.id, runningModule);

    try {
      // Create the bridge API with permission enforcement
      const bridge = this.createBridgeAPI(runningModule);

      // Execute the module code in restricted scope
      const exports = await this.executeInScope(code, bridge);

      return {
        success: true,
        exports,
      };
    } catch (err) {
      // Clean up on error
      this.runningModules.delete(manifest.id);

      return {
        success: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      };
    }
  }

  /**
   * Stop a running module
   */
  stop(moduleId: string): boolean {
    const running = this.runningModules.get(moduleId);
    if (!running) return false;

    running.running = false;
    running.eventHandlers.clear();
    this.runningModules.delete(moduleId);

    return true;
  }

  /**
   * Check if a module is running
   */
  isRunning(moduleId: string): boolean {
    return this.runningModules.has(moduleId);
  }

  /**
   * Get running module info
   */
  getRunningModule(moduleId: string): RunningModule | undefined {
    return this.runningModules.get(moduleId);
  }

  /**
   * Get all running modules
   */
  getRunningModules(): Map<string, RunningModule> {
    return new Map(this.runningModules);
  }

  /**
   * Create the bridge API for a module
   */
  private createBridgeAPI(running: RunningModule): AOSBridgeAPI {
    const { module } = running;
    const { manifest, grantedPermissions } = module;
    const config = this.config;

    const moduleInfo: ModuleInfo = {
      id: manifest.id,
      version: manifest.version,
      permissions: grantedPermissions,
    };

    return {
      module: moduleInfo,

      identity: {
        getPublicKey: async () => {
          if (!hasPermission(grantedPermissions, 'identity:read')) {
            throw permissionError('identity:read');
          }
          if (!config.identity) {
            throw new Error('No identity configured');
          }
          return config.identity.publicKeyBase58;
        },

        sign: async (data: Uint8Array) => {
          if (!hasPermission(grantedPermissions, 'identity:sign')) {
            throw permissionError('identity:sign');
          }
          if (!config.identity) {
            throw new Error('No identity configured');
          }

          // Request user approval
          if (config.onSignRequest) {
            const approved = await config.onSignRequest(manifest.id, data);
            if (!approved) {
              throw new Error('User rejected signing request');
            }
          }

          // Sign the data using createSignedObject
          const signed = createSignedObject(
            'module.signed',
            { data: Array.from(data) },
            config.identity,
            []
          );
          return signed.signature;
        },
      },

      dag: {
        get: async (hash: string) => {
          if (!hasPermission(grantedPermissions, 'dag:read')) {
            throw permissionError('dag:read');
          }
          return config.dagStore.get(hash);
        },

        query: async (filter: DAGFilter) => {
          if (!hasPermission(grantedPermissions, 'dag:read')) {
            throw permissionError('dag:read');
          }

          let objects = await config.dagStore.getAll();

          if (filter.type) {
            objects = objects.filter(o => o.type === filter.type);
          }
          if (filter.creator) {
            objects = objects.filter(o => o.creator === filter.creator);
          }
          if (filter.since) {
            objects = objects.filter(o => o.timestamp >= filter.since!);
          }
          if (filter.until) {
            objects = objects.filter(o => o.timestamp <= filter.until!);
          }

          // Sort by timestamp descending
          objects.sort((a, b) => b.timestamp - a.timestamp);

          if (filter.limit) {
            objects = objects.slice(0, filter.limit);
          }

          return objects;
        },

        put: async (type: string, content: unknown) => {
          if (!hasPermission(grantedPermissions, 'dag:write')) {
            throw permissionError('dag:write');
          }
          if (!config.identity) {
            throw new Error('No identity configured');
          }

          // Prefix type with module namespace
          const namespacedType = `${manifest.id}.${type}`;

          // Use createSignedObject which computes hash
          const signed = createSignedObject(
            namespacedType,
            content as Record<string, unknown>,
            config.identity,
            []
          );

          await config.dagStore.put(signed);

          return signed.hash;
        },
      },

      storage: {
        get: async (key: string) => {
          if (!hasPermission(grantedPermissions, 'storage:local')) {
            throw permissionError('storage:local');
          }
          return running.storage.get(key);
        },

        set: async (key: string, value: unknown) => {
          if (!hasPermission(grantedPermissions, 'storage:local')) {
            throw permissionError('storage:local');
          }
          running.storage.set(key, value);
        },

        delete: async (key: string) => {
          if (!hasPermission(grantedPermissions, 'storage:local')) {
            throw permissionError('storage:local');
          }
          running.storage.delete(key);
        },

        keys: async () => {
          if (!hasPermission(grantedPermissions, 'storage:local')) {
            throw permissionError('storage:local');
          }
          return Array.from(running.storage.keys());
        },
      },

      events: {
        on: (event: string, handler: (...args: unknown[]) => void) => {
          if (!running.eventHandlers.has(event)) {
            running.eventHandlers.set(event, new Set());
          }
          running.eventHandlers.get(event)!.add(handler);
        },

        off: (event: string, handler: (...args: unknown[]) => void) => {
          running.eventHandlers.get(event)?.delete(handler);
        },

        emit: (event: string, ...args: unknown[]) => {
          const handlers = running.eventHandlers.get(event);
          if (handlers) {
            for (const handler of handlers) {
              try {
                handler(...args);
              } catch (err) {
                config.onLog?.(manifest.id, 'error', ['Event handler error:', err]);
              }
            }
          }
        },
      },

      log: {
        debug: (...args: unknown[]) => {
          config.onLog?.(manifest.id, 'debug', args);
        },
        info: (...args: unknown[]) => {
          config.onLog?.(manifest.id, 'info', args);
        },
        warn: (...args: unknown[]) => {
          config.onLog?.(manifest.id, 'warn', args);
        },
        error: (...args: unknown[]) => {
          config.onLog?.(manifest.id, 'error', args);
        },
      },
    };
  }

  /**
   * Execute code in a restricted scope
   *
   * The module only has access to:
   * - AOS bridge API (as `AOS`)
   * - console (for debugging)
   * - Standard JS built-ins (Array, Object, Promise, etc.)
   *
   * No access to:
   * - window/globalThis
   * - document
   * - fetch/XMLHttpRequest
   * - localStorage/sessionStorage
   * - eval (indirectly blocked)
   */
  private async executeInScope(
    code: string,
    bridge: AOSBridgeAPI
  ): Promise<Record<string, unknown>> {
    // Create a module exports object
    const moduleExports: Record<string, unknown> = {};

    // Wrap the code in an async function that returns exports
    const wrappedCode = `
      "use strict";
      return (async function(AOS, exports, console) {
        ${code}
        return exports;
      });
    `;

    try {
      // Create the function (this is the restricted scope)
      // eslint-disable-next-line @typescript-eslint/no-implied-eval
      const createModule = new Function(wrappedCode);
      const moduleFunction = createModule();

      // Create a restricted console
      const restrictedConsole = {
        log: (...args: unknown[]) => bridge.log.info(...args),
        info: (...args: unknown[]) => bridge.log.info(...args),
        warn: (...args: unknown[]) => bridge.log.warn(...args),
        error: (...args: unknown[]) => bridge.log.error(...args),
        debug: (...args: unknown[]) => bridge.log.debug(...args),
      };

      // Execute the module
      await moduleFunction(bridge, moduleExports, restrictedConsole);

      return moduleExports;
    } catch (err) {
      throw new Error(
        `Module execution failed: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Emit an event to a specific module
   */
  emitToModule(moduleId: string, event: string, ...args: unknown[]): boolean {
    const running = this.runningModules.get(moduleId);
    if (!running) return false;

    const handlers = running.eventHandlers.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(...args);
        } catch (err) {
          this.config.onLog?.(moduleId, 'error', ['Event handler error:', err]);
        }
      }
    }

    return true;
  }

  /**
   * Broadcast an event to all running modules
   */
  broadcast(event: string, ...args: unknown[]): void {
    for (const [moduleId] of this.runningModules) {
      this.emitToModule(moduleId, event, ...args);
    }
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create a simple runtime with default configuration
 */
export function createRuntime(
  dagStore: DAGStore,
  identity?: KeyPair
): ModuleRuntime {
  return new ModuleRuntime({
    dagStore,
    identity,
    onLog: (moduleId, level, args) => {
      const prefix = `[${moduleId}]`;
      switch (level) {
        case 'debug':
          console.debug(prefix, ...args);
          break;
        case 'info':
          console.info(prefix, ...args);
          break;
        case 'warn':
          console.warn(prefix, ...args);
          break;
        case 'error':
          console.error(prefix, ...args);
          break;
      }
    },
  });
}

/**
 * Quick test if code is safe to execute (basic checks)
 */
export function isCodeSafe(code: string): { safe: boolean; warnings: string[] } {
  const warnings: string[] = [];

  // Check for obvious dangerous patterns
  const dangerousPatterns = [
    { pattern: /\beval\s*\(/, message: 'Code contains eval()' },
    { pattern: /\bFunction\s*\(/, message: 'Code contains Function constructor' },
    { pattern: /\bimport\s*\(/, message: 'Code contains dynamic import()' },
    { pattern: /\bfetch\s*\(/, message: 'Code attempts to use fetch()' },
    { pattern: /\bXMLHttpRequest\b/, message: 'Code attempts to use XMLHttpRequest' },
    { pattern: /\blocalStorage\b/, message: 'Code attempts to access localStorage' },
    { pattern: /\bsessionStorage\b/, message: 'Code attempts to access sessionStorage' },
    { pattern: /\bdocument\b/, message: 'Code attempts to access document' },
    { pattern: /\bwindow\b/, message: 'Code attempts to access window' },
    { pattern: /\bglobalThis\b/, message: 'Code attempts to access globalThis' },
  ];

  for (const { pattern, message } of dangerousPatterns) {
    if (pattern.test(code)) {
      warnings.push(message);
    }
  }

  return {
    safe: warnings.length === 0,
    warnings,
  };
}
